import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@fmc-digital/i18n";
import { Button, cn } from "@fmc-digital/ui";
import { NpsRatingScale } from "@/components/molecules/NpsRatingScale/NpsRatingScale";
import { FeedbackIcon } from "@/pages/register/icons/FeedbackIcon";
import { submitRegistrationFeedback } from "@/pages/register/feedback/submitRegistrationFeedback";
import { getRegisterFeedbackExitNavigation } from "@/pages/register/feedback/register-feedback.navigation";
import {
  registrationFeedbackMaybeLaterStyles,
  registrationFeedbackNpsActionsStyles,
  registrationFeedbackNpsHeaderStyles,
  registrationFeedbackNpsQuestionStyles,
  registrationFeedbackNpsRootStyles,
  registrationFeedbackNpsScaleSectionStyles,
  registrationFeedbackNpsTitleStyles,
} from "./registration-feedback-nps.styles";

export interface RegistrationFeedbackNpsProps {
  username?: string;
}

export function RegistrationFeedbackNps({ username }: Readonly<RegistrationFeedbackNpsProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rating, setRating] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const navigateAfterFeedback = () => {
    const { route, state } = getRegisterFeedbackExitNavigation(username);
    navigate(route, state ? { state } : undefined);
  };

  const handleMaybeLater = () => {
    navigateAfterFeedback();
  };

  const handleSendFeedback = async () => {
    if (rating === null) {
      return;
    }

    setLoading(true);
    try {
      await submitRegistrationFeedback(rating);
      navigateAfterFeedback();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn(registrationFeedbackNpsRootStyles())} data-testid="registration-feedback-nps">
      <div className={cn(registrationFeedbackNpsHeaderStyles())}>
        <FeedbackIcon />
        <h1 className={cn(registrationFeedbackNpsTitleStyles())}>
          {t("auth.registration.feedback.nps.title")}
        </h1>
        <p className={cn(registrationFeedbackNpsQuestionStyles())}>
          {t("auth.registration.feedback.nps.question")}
        </p>
      </div>

      <div className={cn(registrationFeedbackNpsScaleSectionStyles())}>
        <NpsRatingScale
          value={rating}
          onChange={setRating}
          ariaLabel={t("auth.registration.feedback.nps.scaleAriaLabel")}
          lowLabel={t("auth.registration.feedback.nps.lowLabel")}
          highLabel={t("auth.registration.feedback.nps.highLabel")}
        />
      </div>

      <div className={cn(registrationFeedbackNpsActionsStyles())}>
        <Button
          variant="filled"
          fullWidth
          onClick={handleSendFeedback}
          disabled={rating === null || loading}
          data-testid="feedback-nps-send-button"
          aria-label={t("auth.registration.feedback.nps.sendFeedbackAriaLabel")}
        >
          {loading ? t("auth.registration.feedback.nps.submitting") : t("auth.registration.feedback.nps.sendFeedback")}
        </Button>
        <button
          type="button"
          className={cn(registrationFeedbackMaybeLaterStyles())}
          onClick={handleMaybeLater}
          data-testid="feedback-nps-maybe-later-button"
          aria-label={t("auth.registration.feedback.nps.maybeLaterAriaLabel")}
        >
          {t("auth.registration.feedback.nps.maybeLater")}
        </button>
      </div>
    </div>
  );
}
